import { Component } from '@angular/core';
import { createSelector, Store } from '@ngrx/store';

import { FreyaCommonModule } from '../../../freya-common/freya-common.module';
import { SharedModule } from '../../../shared/shared.module';
import { JobCreateCustomerActions } from '../../job-create/job-create-customer/job-create-customer.actions';
import { JobCreateCustomerComponent } from '../../job-create/job-create-customer/job-create-customer.component';
import { jobCreateCustomerFeature } from '../../job-create/job-create-customer/job-create-customer.feature';
import { EMAIL_PATTERN, PHONE_PATTERN } from '../../job-helpers';
import {
  jobToolFeature,
  selectJob,
  selectJobInCurrentZone,
  selectCustomer,
  selectCompanyName,
  selectJobMetadata,
  selectJobSource as customerMutating,
  selectJobLoaded
} from '../../job-tool.reducer';
import { JobTimelinePipe } from '../job-timeline.pipe';
import { overviewLocationsFeature } from '../overview-locations/overview-locations.feature';

const selectButtonDisabledMessage = createSelector(
  jobCreateCustomerFeature.selectCustomerEditor,
  overviewLocationsFeature.selectLocationEditors,
  ({ fullName, email, phone }, { billing }) => {

    if (!fullName?.trim()) {
      return 'Name is required';
    }

    if (email && !EMAIL_PATTERN.test(email)) {
      return 'Invalid email format';
    }

    if (phone && !PHONE_PATTERN.test(phone)) {
      return 'Invalid phone format';
    }

    if (billing?.address && !billing.selectedLocation) {
      return 'Invalid billing address';
    }
  }
);

@Component({
  selector: 'app-overview-customer',
  standalone: true,
  imports: [
    FreyaCommonModule,
    SharedModule,
    JobTimelinePipe,
    JobCreateCustomerComponent
  ],
  templateUrl: './overview-customer.component.html',
  styleUrls: [
    './overview-customer.component.scss',
    '../overview-header/overview-chip/shared.overview-chip.component.scss',
  ],
})
export class OverviewCustomerComponent {

  public viewModel$ = this.store.select(createSelector({
    customerName: createSelector(
      selectCustomer,
      jobCreateCustomerFeature.selectIsCustomerEditMode,
      jobCreateCustomerFeature.selectCustomerEditor,
      (currentCustomer, isCustomerEditMode, { fullName, company, selectedCustomer }) => {

        const segments: string[] = [];

        if (!isCustomerEditMode) {

          if (!currentCustomer) {
            return 'No Customer';
          }

          const currentCustomerName = [
            currentCustomer.givenName,
            currentCustomer.familyName,
          ]
            .map((s) => s?.trim())
            .filter(Boolean)
            .join(' ');

          segments.push(currentCustomerName);

          if (currentCustomer.company) {
            segments.push(currentCustomer.company);
          }

          return segments.join(' | ');
        }

        if (selectedCustomer) {

          const selectedCustomerName = [
            selectedCustomer.givenName,
            selectedCustomer.familyName,
          ]
            .map((s) => s?.trim())
            .filter(Boolean)
            .join(' ');

          segments.push(selectedCustomerName);

          if (selectedCustomer.company) {
            segments.push(selectedCustomer.company);
          }

          return segments.join(' | ');
        }

        if (!fullName.trim()) {
          return 'New Customer';
        }

        segments.push(fullName.trim());

        if (company) {
          segments.push(company);
        }

        return segments.join(' | ');
      }
    ),
    showNewCustomerTag: createSelector(
      jobCreateCustomerFeature.selectIsCustomerEditMode,
      jobCreateCustomerFeature.selectCustomerEditor,
      (isCustomerEditMode, { selectedCustomer, fullName }) => isCustomerEditMode && !selectedCustomer && Boolean(fullName.trim()),
    ),
    company: selectCompanyName,
    jobLoaded: selectJobLoaded,
    customer: selectCustomer,
    job: selectJob,
    jobMetadata: selectJobMetadata,
    jobSource: customerMutating,
    jobNotInZone: createSelector(selectJobInCurrentZone, (inZone) => !inZone),
    buttonDisabledMessage: selectButtonDisabledMessage,
    isEditMode: jobCreateCustomerFeature.selectIsCustomerEditMode,
    editButtonDisabled: createSelector(
      selectJobLoaded,
      jobToolFeature.selectCustomerMutating,
      selectJobInCurrentZone,
      (jobLoaded, customerLoading, jobInCurrentZone) => {
        return !jobLoaded || customerLoading || !jobInCurrentZone;
      }
    ),
    mutating: jobToolFeature.selectCustomerMutating,
  }));

  public actions = JobCreateCustomerActions;

  constructor(
    public store: Store,
  ) { }
}
